import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ApiService } from 'app/api.service';
import { SalesReturnDetail, SalesReturnPayload } from '../create-voucher-posting-dialog/interface/voucher.interface';
import { UiPanelAction, UiPanelMode } from 'app/shared/ui/models/ui-document-panel.model';
import { UI_DOCUMENT_PANEL_BREADCRUMBS } from 'app/shared/ui/document-panel/document-panel.config';

@Component({
  selector: 'app-sales-purchase-return-dialog',
  templateUrl: './sales-purchase-return-dialog.component.html',
  styleUrls: ['./sales-purchase-return-dialog.component.scss'],
  host: { class: 'ui-document-panel-host' },
})
export class SalesPurchaseReturnDialogComponent implements OnInit {
  voucherNo = '';
  date = '';
  isView = false;
  isUpdate = false;
  id: any;
  type = 'Sales Return';
  panelMode: UiPanelMode = 'create';
  branch: any;
  profit_cost_id: any;
  invoice_type = '';
  salesInvoice: any;
  sales_invoice: any[] = [];
  profit_cost: any[] = [];
  branch_data: any[] = [];
  customers = '';
  cutomer_branchs = '';
  invoice_date = '';
  vendors = '';
  vendor_branchs = '';
  purchaseInvoice: any;
  creditNote: any;
  sales_invoice_details: SalesReturnDetail[] = [];
  purchase_invoice_details: SalesReturnDetail[] = [];
  credit_note_details: SalesReturnDetail[] = [];
  debit_note_details: SalesReturnDetail[] = [];
  debitNote: any;
  debit_note: any[] = [];
  purchase_invoice: any[] = [];
  credit_note: any[] = [];

  constructor(
    private apiService: ApiService,
    public dialogRef: MatDialogRef<SalesPurchaseReturnDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.id = data?.id;
    if (data?.docType) {
      this.type = data.docType;
    } else if (data?.type === 'Sales Return' || data?.type === 'Purchase Return') {
      this.type = data.type;
    }
    this.initPanelMode();
    this.branch = data?.branch_id;
    this.voucherNo = data?.voucherNo;
    this.date = data?.date || new Date().toISOString().split('T')[0];
  }

  ngOnInit(): void {
    this.fetchbranchData();
    if (this.id) {
      this.fetchVoucherById();
    }
  }

  private initPanelMode(): void {
    const mode = this.data?.type;
    if (mode === 'View' || this.data?.operationType === 'view') {
      this.panelMode = 'view';
      this.isView = true;
      this.isUpdate = true;
    } else if (mode === 'Update' || this.data?.operationType === 'update') {
      this.panelMode = 'edit';
      this.isView = false;
      this.isUpdate = true;
    } else {
      this.panelMode = 'create';
      this.isView = false;
      this.isUpdate = false;
    }
  }

  get breadcrumb(): string {
    return this.type === 'Purchase Return'
      ? UI_DOCUMENT_PANEL_BREADCRUMBS.purchaseReturn
      : UI_DOCUMENT_PANEL_BREADCRUMBS.salesReturn;
  }

  get panelTitle(): string {
    const label = this.type === 'Purchase Return' ? 'Purchase Return' : 'Sales Return';
    switch (this.panelMode) {
      case 'create': return `Create ${label}`;
      case 'edit': return `Edit ${label}`;
      case 'view': return `View ${label}`;
      default: return label;
    }
  }

  get lineItems(): SalesReturnDetail[] {
    switch (this.invoice_type) {
      case 'sales': return this.sales_invoice_details;
      case 'debit note': return this.debit_note_details;
      case 'purchase': return this.purchase_invoice_details;
      case 'credit note': return this.credit_note_details;
      default: return [];
    }
  }

  get showLineItems(): boolean {
    return !!this.invoice_type && this.lineItems.length > 0;
  }

  get isSalesParty(): boolean {
    return this.invoice_type === 'sales' || this.invoice_type === 'debit note';
  }

  get footerPrimaryActions(): UiPanelAction[] {
    if (this.panelMode === 'view') {
      return [{ id: 'edit', label: 'Edit', icon: 'edit', variant: 'primary', visible: true }];
    }
    return [{
      id: 'save',
      label: this.id ? 'Update' : 'Create',
      icon: 'save',
      variant: 'success',
      visible: true,
    }];
  }

  get footerSecondaryActions(): UiPanelAction[] {
    return [{
      id: 'cancel',
      label: this.panelMode === 'view' ? 'Close' : 'Cancel',
      icon: 'close',
      variant: 'ghost',
      visible: true,
    }];
  }

  get footerDestructiveAction(): UiPanelAction | null {
    if (this.panelMode !== 'view' || !this.id) return null;
    return { id: 'delete', label: 'Delete', icon: 'delete', variant: 'danger', visible: true };
  }

  get footerOverflowActions(): UiPanelAction[] {
    return [];
  }

  onClose(): void {
    this.dialogRef.close();
  }

  onCancel(): void {
    if (this.panelMode === 'edit' && this.data?.type === 'View') {
      this.enterViewMode();
      return;
    }
    this.dialogRef.close();
  }

  enterEditMode(): void {
    this.panelMode = 'edit';
    this.isView = false;
    this.isUpdate = true;
    if (this.data) this.data.type = 'Update';
  }

  enterViewMode(): void {
    this.panelMode = 'view';
    this.isView = true;
    this.isUpdate = true;
    if (this.data) this.data.type = 'View';
    if (this.id) this.fetchVoucherById();
  }

  onFooterAction(actionId: string): void {
    switch (actionId) {
      case 'save': this.save(); break;
      case 'edit': this.enterEditMode(); break;
      case 'delete': this.deleteReturn(); break;
      case 'cancel': this.onCancel(); break;
    }
  }

  save(): void {
    if (this.type === 'Sales Return') {
      this.saveSalesReturn();
    } else {
      this.savePurchaseReturn();
    }
  }

  deleteReturn(): void {
    if (!this.id || !confirm('Are you sure you want to delete this return?')) return;
    const requestData = {
      doc_type: this.type,
      doc_id: this.id,
      type: 'delete',
    };
    this.apiService.deleteVoucherPosting(this.id, requestData).subscribe(
      () => {
        alert('Return deleted successfully');
        this.dialogRef.close(true);
      },
      (error) => alert('Error deleting return: ' + error.error?.message)
    );
  }

  onReturnTypeChange(value: string) {
    this.type = value;
    this.invoice_type = '';
    this.clearInvoiceData();
  }

  fetchVoucherNo(data: any) {
    this.apiService.getVoucher(data).subscribe(
      (response) => {
        this.voucherNo = response.voucher_no;
      },
      (error) => {
        console.error('Error fetching voucher number:', error);
      }
    );
  }

  fetchbranchData() {
    this.apiService.getbranchData().subscribe((response) => {
      this.branch_data = response;
    });
  }

  loadInvoices() {
    if (!this.branch) return;
    if (this.invoice_type === 'sales') this.fetchSalesInvoice();
    else if (this.invoice_type === 'debit note') this.fetchDebitNote();
    else if (this.invoice_type === 'purchase') this.fetchPurchaseInvoice();
    else if (this.invoice_type === 'credit note') this.fetchCreditNote();
  }

  onInvoiceTypeChange(value: string) {
    this.invoice_type = value;
    this.clearInvoiceData();
    this.loadInvoices();
  }

  onBranchChange(branchId: any) {
    this.branch = branchId;
    this.clearInvoiceData();
    this.loadInvoices();
  }

  clearInvoiceData() {
    this.salesInvoice = null;
    this.purchaseInvoice = null;
    this.debitNote = null;
    this.creditNote = null;
    this.profit_cost_id = null;
    this.profit_cost = [];
    this.sales_invoice = [];
    this.purchase_invoice = [];
    this.debit_note = [];
    this.credit_note = [];
    this.sales_invoice_details = [];
    this.purchase_invoice_details = [];
    this.debit_note_details = [];
    this.credit_note_details = [];
    this.customers = '';
    this.cutomer_branchs = '';
    this.vendors = '';
    this.vendor_branchs = '';
  }

  limitDecimal(event: any, digits: number) {
    let value = event.target.value;
    if (value.includes('.')) {
      const parts = value.split('.');
      if (parts[1].length > digits) {
        parts[1] = parts[1].substring(0, digits);
        value = parts.join('.');
        event.target.value = value;
      }
    }
  }

  fetchSalesInvoice(salesPurchaseId: any = null, voucherData: any = null) {
    const postData = {
      Invoice_type: this.invoice_type,
      branch_id: this.branch
    };
    this.apiService.getPi(postData).subscribe(
      (response) => {
        this.sales_invoice = response;
        if (salesPurchaseId) {
          this.salesInvoice = salesPurchaseId;
          this.fetchProfitCostFromInvoice(this.invoice_type, this.salesInvoice);
          this.fetchSalesInvoiceDetails(this.salesInvoice, voucherData);
        }
      },
      (error) => {
        console.error('Error fetching sales invoice:', error);
      }
    );
  }

  fetchDebitNote(salesPurchaseId: any = null, voucherData: any = null) {
    const postData = {
      Invoice_type: this.invoice_type,
      branch_id: this.branch
    };
    this.apiService.getPi(postData).subscribe(
      (response) => {
        this.debit_note = response;
        if (salesPurchaseId) {
          this.debitNote = salesPurchaseId;
          this.fetchProfitCostFromInvoice(this.invoice_type, this.debitNote);
          this.fetchDebitNoteDetails(this.debitNote, voucherData);
        }
      },
      (error) => {
        console.error('Error fetching debit note:', error);
      }
    );
  }

  fetchPurchaseInvoice(salesPurchaseId: any = null, voucherData: any = null) {
    const postData = {
      Invoice_type: this.invoice_type,
      branch_id: this.branch
    };
    this.apiService.getPi(postData).subscribe(
      (response) => {
        this.purchase_invoice = response;
        if (salesPurchaseId) {
          this.purchaseInvoice = salesPurchaseId;
          this.fetchProfitCostFromInvoice(this.invoice_type, this.purchaseInvoice);
          this.fetchPurchaseInvoiceDetails(this.purchaseInvoice, voucherData);
        }
      },
      (error) => {
        console.error('Error fetching purchase invoice:', error);
      }
    );
  }

  fetchCreditNote(salesPurchaseId: any = null, voucherData: any = null) {
    const postData = {
      Invoice_type: this.invoice_type,
      branch_id: this.branch
    };
    this.apiService.getPi(postData).subscribe(
      (response) => {
        this.credit_note = response;
        if (salesPurchaseId) {
          this.creditNote = salesPurchaseId;
          this.fetchProfitCostFromInvoice(this.invoice_type, this.creditNote);
          this.fetchCreditNoteDetails(this.creditNote, voucherData);
        }
      },
      (error) => {
        console.error('Error fetching credit note:', error);
      }
    );
  }

  onInvoiceChange(invoiceId: any) {
    if (this.invoice_type === 'sales') {
      this.salesInvoice = invoiceId;
      this.fetchProfitCostFromInvoice(this.invoice_type, invoiceId);
      this.fetchSalesInvoiceDetails(invoiceId);
    } else if (this.invoice_type === 'purchase') {
      this.purchaseInvoice = invoiceId;
      this.fetchProfitCostFromInvoice(this.invoice_type, invoiceId);
      this.fetchPurchaseInvoiceDetails(invoiceId);
    } else if (this.invoice_type === 'credit note') {
      this.creditNote = invoiceId;
      this.fetchProfitCostFromInvoice(this.invoice_type, invoiceId);
      this.fetchCreditNoteDetails(invoiceId);
    } else if (this.invoice_type === 'debit note') {
      this.debitNote = invoiceId;
      this.fetchProfitCostFromInvoice(this.invoice_type, invoiceId);
      this.fetchDebitNoteDetails(invoiceId);
    }
  }

  fetchProfitCostFromInvoice(invoice_type: string, invoice_id: any) {
    const selectedProfitCost = this.profit_cost_id;
    const postData = { invoice_type: invoice_type, invoice_id: invoice_id };
    this.apiService.ProfitCostByInvoiceId(postData).subscribe(
      (response) => {
        this.profit_cost = response || [];
        if (selectedProfitCost?.length) {
          this.profit_cost_id = selectedProfitCost;
        }
      },
      (error) => {
        console.error('Error fetching profit cost:', error);
      }
    );
  }

  onProfitCostChange(profitCostId: any) {
    this.profit_cost_id = profitCostId;
    if (this.invoice_type === 'sales') {
      this.fetchSalesInvoiceDetails(this.salesInvoice);
    } else if (this.invoice_type === 'debit note') {
      this.fetchDebitNoteDetails(this.debitNote);
    } else if (this.invoice_type === 'purchase') {
      this.fetchPurchaseInvoiceDetails(this.purchaseInvoice);
    } else if (this.invoice_type === 'credit note') {
      this.fetchCreditNoteDetails(this.creditNote);
    }
  }

  fetchSalesInvoiceDetails(Invoice_Id: number, savedDetails: any[] = []) {
    const data = {
      Invoice_Id: Invoice_Id,
      profit_cost_id: this.profit_cost_id
    };
    this.apiService.getVoucherDetailsByProfitCenter(data).subscribe(
      (response: any[]) => {
        this.sales_invoice_details = response || [];
        const first = this.sales_invoice_details[0];
        if (first) {
          this.customers = first.customer_name;
          this.cutomer_branchs = first.customer_branch_name;
          this.invoice_date = first.sales_invoice_date;
        }
        if (savedDetails?.length > 0) {
          this.sales_invoice_details.forEach(item => {
            const match = savedDetails.find(d =>
              Number(d.purchase_voucher_details_id) === Number(item.id)
            );
            if (match) {
              item.change_qty = match.change_qty || 0;
              item.change_value = +(item.change_qty * item.unit_price).toFixed(2);
              item.pending_qty = Number(item.pending_qty) + Number(item.change_qty);
            }
          });
        }
      }
    );
  }

  fetchPurchaseInvoiceDetails(Invoice_Id: number, savedDetails: any[] = []) {
    const data = {
      Invoice_Id: Invoice_Id,
      profit_cost_id: this.profit_cost_id
    };
    this.apiService.getVoucherDetailsByProfitCenter(data).subscribe(
      (response: any[]) => {
        this.purchase_invoice_details = response || [];
        if (!this.purchase_invoice_details.length) return;
        const first = this.purchase_invoice_details[0];
        this.vendors = first.vendor_name;
        this.vendor_branchs = first.vendor_branch_name;
        this.invoice_date = first.purchase_invoice_date;
        if (savedDetails?.length > 0) {
          this.purchase_invoice_details.forEach(item => {
            const match = savedDetails.find(d =>
              Number(d.purchase_voucher_details_id) === Number(item.id)
            );
            if (match) {
              item.change_qty = match.change_qty || 0;
              item.change_value = +(item.change_qty * item.unit_price).toFixed(2);
              item.pending_qty = Number(item.pending_qty) + Number(item.change_qty);
            }
          });
        }
      }
    );
  }

  fetchDebitNoteDetails(Invoice_Id: number, savedDetails: any[] = []) {
    const data = {
      Invoice_Id: Invoice_Id,
      profit_cost_id: this.profit_cost_id
    };
    this.apiService.getVoucherDetailsByProfitCenter(data).subscribe(
      (response: any) => {
        this.debit_note_details = response || [];
        if (this.debit_note_details.length > 0) {
          const first = this.debit_note_details[0];
          this.customers = first.customer_name;
          this.cutomer_branchs = first.customer_branch_name;
          this.invoice_date = first.sales_invoice_date;
          if (savedDetails?.length > 0) {
            this.debit_note_details.forEach(item => {
              const match = savedDetails.find(d =>
                Number(d.purchase_voucher_details_id) === Number(item.id)
              );
              if (match) {
                item.change_qty = match.change_qty || 0;
                item.change_value = +(item.change_qty * item.unit_price).toFixed(2);
                item.pending_qty = Number(item.pending_qty) + Number(item.change_qty);
              }
            });
          }
        }
      }
    );
  }

  fetchCreditNoteDetails(Invoice_Id: number, savedDetails: any[] = []) {
    const data = { Invoice_Id: Invoice_Id, profit_cost_id: this.profit_cost_id };
    this.apiService.getVoucherDetailsByProfitCenter(data).subscribe(
      (response: any) => {
        this.credit_note_details = response || [];
        if (this.credit_note_details.length > 0) {
          const first = this.credit_note_details[0];
          this.vendors = first.vendor_name;
          this.vendor_branchs = first.vendor_branch_name;
          this.invoice_date = first.sales_invoice_date;
          if (savedDetails?.length > 0) {
            this.credit_note_details.forEach(item => {
              const match = savedDetails.find(d =>
                Number(d.purchase_voucher_details_id) === Number(item.id)
              );
              if (match) {
                item.change_qty = match.change_qty || 0;
                item.change_value = +(item.change_qty * item.unit_price).toFixed(2);
                item.pending_qty = Number(item.pending_qty) + Number(item.change_qty);
              }
            });
          }
        }
      }
    );
  }

  fetchVoucherById() {
    this.apiService.getSalesPurchaseReturnById(this.id).subscribe((res: any) => {
      if (!res?.status || !res?.data?.return) return;
      const v = res.data.return;
      const details = res.data.details || [];
      this.type = v.type;
      this.voucherNo = v.voucher_no;
      this.date = v.date;
      this.branch = v.branch_id;
      this.profit_cost_id = v.profit_cost_id
        ? v.profit_cost_id.toString().split(',').map((id: any) => Number(id))
        : [];
      if (this.type === 'Sales Return') {
        this.invoice_type = v.sales_purchase_id ? 'sales' : 'debit note';
        if (this.invoice_type === 'sales') {
          this.salesInvoice = v.sales_purchase_id;
          this.fetchSalesInvoice(this.salesInvoice, details);
        } else {
          this.debitNote = v.credit_debit_id;
          this.fetchDebitNote(this.debitNote, details);
        }
      }
      if (this.type === 'Purchase Return') {
        this.invoice_type = v.sales_purchase_id ? 'purchase' : 'credit note';
        if (this.invoice_type === 'purchase') {
          this.purchaseInvoice = v.sales_purchase_id;
          this.fetchPurchaseInvoice(this.purchaseInvoice, details);
        } else {
          this.creditNote = v.credit_debit_id;
          this.fetchCreditNote(this.creditNote, details);
        }
      }
    });
  }

  ReturnQtyChange(item: SalesReturnDetail, returnQty: any) {
    const qty = Number(returnQty) || 0;
    if (qty > item.pending_qty) {
      alert('Return quantity cannot be more than pending quantity');
      item.change_qty = 0;
      return;
    }
    item.change_qty = qty;
    item.change_value = +(qty * Number(item.unit_price || 0)).toFixed(2);
  }

  saveSalesReturn(): void {
    const sourceDetails = this.invoice_type === 'debit note' ? this.debit_note_details : this.sales_invoice_details;
    const filteredItems = sourceDetails.filter(item => (item.change_qty || 0) > 0);
    if (filteredItems.length === 0) {
      alert('Please enter return quantity for at least one item');
      return;
    }
    const postData: SalesReturnPayload = {
      voucher_no: this.voucherNo,
      date: this.date,
      doc_type: 'Sales Return',
      details: filteredItems,
      branch_id: this.branch,
      profit_cost_id: Array.isArray(this.profit_cost_id) ? this.profit_cost_id.join(',') : this.profit_cost_id,
      sales_purchase_id: this.invoice_type === 'debit note' ? null : this.salesInvoice,
      invoice_type: this.invoice_type,
      credit_debit_id: this.invoice_type === 'debit note' ? this.debitNote : null,
      type: this.type,
    };
    const apiCall = this.id
      ? this.apiService.updateSalesPurchaseReturn(this.id, postData)
      : this.apiService.createSalesPurchaseReturn(postData);

    apiCall.subscribe(
      () => {
        alert(this.id ? 'Sales Return updated successfully' : 'Sales Return created successfully');
        this.dialogRef.close(true);
      },
      (error) => {
        alert('Error saving voucher: ' + error.error.message);
      }
    );
  }

  savePurchaseReturn(): void {
    const sourceDetails = this.invoice_type === 'credit note'
      ? this.credit_note_details
      : this.purchase_invoice_details;
    const filteredItems = sourceDetails.filter(item => (item.change_qty || 0) > 0);
    if (filteredItems.length === 0) {
      alert('Please enter return quantity for at least one item');
      return;
    }
    const postData: SalesReturnPayload = {
      voucher_no: this.voucherNo,
      date: this.date,
      doc_type: 'Purchase Return',
      details: filteredItems,
      branch_id: this.branch,
      profit_cost_id: this.profit_cost_id,
      sales_purchase_id: this.invoice_type === 'credit note' ? null : this.purchaseInvoice,
      invoice_type: this.invoice_type,
      credit_debit_id: this.invoice_type === 'credit note' ? this.creditNote : null,
      type: this.type,
    };
    const apiCall = this.id
      ? this.apiService.updateSalesPurchaseReturn(this.id, postData)
      : this.apiService.createSalesPurchaseReturn(postData);

    apiCall.subscribe(
      () => {
        alert(this.id ? 'Purchase Return updated successfully' : 'Purchase Return created successfully');
        this.dialogRef.close(true);
      },
      (error) => {
        alert('Error saving Purchase Return: ' + error.error.message);
      }
    );
  }
}
